// ---------------------------------------------------------------------------
// Platform Fit · Platform assessment for Instagram strategy
// ---------------------------------------------------------------------------

export type PlatformId = 'linkedin' | 'twitter' | 'tiktok' | 'youtube' | 'facebook';
export type InstagramFormat = 'reels' | 'carousels' | 'static_posts';
export type PlatformVerdict = 'strong' | 'good' | 'weak' | 'poor';

export const PLATFORM_DISPLAY: Record<PlatformId, string> = {
  linkedin: 'LinkedIn',
  twitter: 'X (Twitter)',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  facebook: 'Facebook',
};

export const FORMAT_DISPLAY: Record<InstagramFormat, string> = {
  reels: 'Reels',
  carousels: 'Carousels',
  static_posts: 'Static Posts',
};

export interface PlatformFit {
  instagram_score: number;
  verdict: PlatformVerdict;
  reasoning: string;
  best_formats: InstagramFormat[];
  better_platforms: PlatformId[];
  suggestion: string | null;
}

// ---------------------------------------------------------------------------
// Bootstrap · Niche Bootstrap Wizard types
// ---------------------------------------------------------------------------

// Recommendation keys: EN canonical + DE legacy for backward compatibility
export type BootstrapRecommendation = 'recommended' | 'neutral' | 'warning' | 'empfohlen' | 'warnung';

export interface BootstrapOption {
  title: string;
  description: string;
  recommendation: BootstrapRecommendation;
  reasoning: string;
}

export interface BootstrapAnalysis {
  options: BootstrapOption[];
  general_feedback: string | null;
  platform_fit?: PlatformFit;
}

export interface BootstrapNicheRequest {
  chosen_option: BootstrapOption | null;
  custom_direction: string | null;
  briefing: string;
}

export interface BootstrapNicheResponse {
  niche_data: Record<string, unknown>;
}

export type BootstrapPhase =
  | 'idle'
  | 'input'
  | 'analyzing'
  | 'options'
  | 'generating'
  | 'sequential'
  | 'complete';

export interface BootstrapState {
  bootstrapPhase: BootstrapPhase;
  bootstrapNiche: string;
  bootstrapBriefing: string;
  bootstrapAnalysis: BootstrapAnalysis | null;
  bootstrapChosenOption: BootstrapOption | null;
  bootstrapCustomDirection: string | null;
  bootstrapLoading: boolean;
  bootstrapError: string | null;
}

// ---------------------------------------------------------------------------
// Cascade / Autofill reliability types
// ---------------------------------------------------------------------------

export type ModuleStepStatus = 'pending' | 'running' | 'complete' | 'failed' | 'skipped';

export interface CascadeStatus {
  market: ModuleStepStatus;
  avatar: ModuleStepStatus;
  mvo: ModuleStepStatus;
  offer: ModuleStepStatus;
  message: ModuleStepStatus;
  visual: ModuleStepStatus;
}

export interface AutofillResult {
  modules_data: Record<string, unknown>;
  cascade_status: CascadeStatus;
  failed_module: string | null;
  error_detail: string | null;
}

export interface ModuleConfig {
  key_fields: string[];
  defaults: Record<string, unknown>;
  upstream_deps: string[];
  field_labels: Record<string, string>;
}

// ---------------------------------------------------------------------------

export interface User {
  id: number;
  username: string;
  role: 'admin' | 'worker' | 'setter' | 'setter-junior' | 'head_of_sales' | 'fraud0_setter';
  lang: string;
  display_name?: string;
  avatar_color?: string;
  must_change_password?: number;
  is_active?: number;
  // Komma-Kette ('de,en') · Sprachen, in denen dieses Konto ANRUFEN kann. Steht auch im
  // angemeldeten Nutzer, weil der fraud0-Stapel danach sortiert (useLeadQueue). Nicht zu
  // verwechseln mit `lang` oben (Sprache der Oberfläche). Leer/fehlend = nur Deutsch.
  call_languages?: string;
}

export interface WebUser {
  id: number;
  username: string;
  role: 'admin' | 'worker' | 'setter' | 'setter-junior' | 'head_of_sales' | 'fraud0_setter';
  lang: string;
  display_name: string | null;
  is_active: number;
  must_change_password: number;
  avatar_color: string | null;
  created_at: string;
  video_outpaint_enabled?: number;
  video_outpaint_calls?: number;
  video_outpaint_period?: string;
  // Komma-Kette ('de,en'). Sprachen, in denen dieses Konto ANRUFEN kann — nicht zu
  // verwechseln mit `lang` oben (Sprache der Oberflaeche). Leer = nur Deutsch.
  call_languages?: string;
}

export interface ActivityEntry {
  id: number;
  user_id: number;
  username: string;
  method: string;
  endpoint: string;
  status_code: number | null;
  created_at: string;
}

export interface LeaderboardEntry2 {
  user_id: number;
  username: string;
  total_actions: number;
  calls_made: number;
}

export interface NicheAssignment {
  id: number;
  niche: string;
  language: string;
  owner_user_id: number | null;
  claimed_at: string;
  username: string | null;
  display_name: string | null;
  avatar_color: string | null;
}

export type AccountPlatform = 'instagram' | 'tiktok' | 'youtube' | 'linkedin' | 'x' | 'website';

export const PLATFORM_META: Record<AccountPlatform, { label: string }> = {
  instagram: { label: 'IG' },
  tiktok:    { label: 'TT' },
  youtube:   { label: 'YT' },
  linkedin:  { label: 'LI' },
  x:         { label: 'X'  },
  website:   { label: 'WEB' },
};

export interface AccountStrategy {
  strategy_id: number;
  strategy_name: string;
  niche: string | null;
}

export interface Account {
  id: number;
  username: string;
  platform: AccountPlatform;
  ig_url: string;
  growth: number | null;
  followers: number | null;
  following: number | null;
  posts_count: number | null;
  bio: string | null;
  niche: string | null;
  is_tracked: number;
  account_type: string;
  classification: string;
  scrape_tier: number;
  is_hidden: number;
  is_own: number;
  last_scraped_at: string | null;
  created_at: string;
  logo_url: string | null;
  profile_pic_url: string | null;
  strategies?: AccountStrategy[];
}

export interface LogoOption {
  svg: string;
  description: string;
}

export interface SparklinePoint { day: string; value: number }
export interface OwnAccountInNiche { id: number; username: string; followers: number | null; growth: number | null }
export interface OwnNiche { name: string; accounts: number; followers: number; avg_growth: number; account_list: OwnAccountInNiche[] }

export interface NicheInfo {
  name: string;
  count: number;
}

export interface NicheContentInfo {
  name: string;
  total: number;
  usable: number;
}

export interface Post {
  id: number;
  account_id: number;
  owner_username: string;
  caption: string;
  likes: number;
  comments: number;
  views: number;
  viral_score: number;
  url: string;
  posted_at: string;
  scraped_at: string;
  image_url: string;
  post_type: string;
  used_at: string | null;
  has_rewrite?: boolean;
  queue_status?: string | null;
}

export interface PostDetail extends Post {
  image_text: string | null;
  hashtags: string | null;
  rewrite: { rewritten_text: string; hashtags: string | null } | null;
}

export interface QueueItem {
  id: number;
  post_id: number | null;
  rewrite_id: number | null;
  manager_id: number | null;
  niche: string;
  status: 'pending' | 'sent' | 'posted' | 'problem' | 'skipped';
  caption: string | null;
  image_url: string | null;
  hashtags: string | null;
  assigned_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface Manager {
  id: number;
  name: string;
  telegram_chat_id: string | null;
  web_user_id: number | null;
  niches: string[];
  created_at: string;
}

export interface Profile {
  id: number;
  name: string;
  niche: string | null;
  language: string;
  directive_path: string | null;
  created_at: string;
}

export interface Stats {
  account_pipeline: {
    total_accounts: number;
    pending_review: number;
    stale_accounts: number;
    active_niches: number;
  };
  content_throughput: {
    total_posts: number;
    posts_used: number;
    rewrites_generated: number;
    queue_backlog: Record<string, number>;
  };
  own_metrics: {
    total_followers: number;
    account_count: number;
    avg_growth: number;
    engagement_rate: number;
    published_this_week: number;
    sparklines: {
      followers: SparklinePoint[];
      engagement: SparklinePoint[];
      growth: SparklinePoint[];
    };
  };
  own_niches: OwnNiche[];
  growth_metrics: {
    total_audience: number;
    avg_growth: number;
    niches: Array<{
      name: string;
      accounts: number;
      followers: number;
      avg_growth: number;
    }>;
  };
}

export type KanbanColumn = 'scraped' | 'ready' | 'assigned' | 'done';

// ---------------------------------------------------------------------------
// Strategy OS types
// ---------------------------------------------------------------------------

export type StrategyStatus = 'active' | 'paused' | 'archived' | 'evolved';

export interface ToneStylePreferences {
  aggressiveness: number;
  aspectFocus: 'pain' | 'desire' | 'transformation';
  toneFormality: number;
  mehrwert: number;
  /** Kontrast-Metrik-Modus: akkumulierender Ist-vs-Ziel-Kontrast. */
  metricAccumulation?: boolean;
}

export interface Strategy {
  id: number;
  name: string;
  niche: string | null;
  status: StrategyStatus;
  parent_strategy_id: number | null;
  generation: number;
  current_step: number;
  account_language: string;
  category: string | null;
  target_country: string | null;
  last_translated_at: string | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  platform_verdict: PlatformVerdict | null;
  tone_style_preferences?: ToneStylePreferences;
}

export interface ModuleWorkingData {
  [key: string]: unknown;
}

export interface ModuleData {
  workingData: ModuleWorkingData;
  currentVersion?: number;
}

export interface StrategyDetail extends Strategy {
  modules_data: Record<string, ModuleData>;
  outdated_flags: Record<string, boolean>;
  upstream_hints: Record<string, string[]>;
}

export interface ModuleVersion {
  id: number;
  strategy_id: number;
  module_id: string;
  version: number;
  data: ModuleWorkingData;
  saved_at: string;
}

export type AIFeedbackType = 'positive' | 'warning' | 'critical';

export interface FieldFeedback {
  status: 'ok' | 'warning' | 'critical' | 'missing';
  suggestion?: string;
}

export interface AIFeedback {
  id?: number;
  text: string;
  type: AIFeedbackType;
  fields?: Record<string, FieldFeedback>;
  model_used?: string;
}

export interface RealityCheckEntry {
  id: number;
  module_id: string;
  verdict: AIFeedbackType;
  summary: string;
  field_feedback: Record<string, FieldFeedback>;
  module_snapshot: Record<string, unknown>;
  model_used: string;
  created_at: string;
}

export interface StrategyAccount {
  id: number;
  account_id?: number;
  strategy_id: number;
  assigned_at: string;
  ended_at: string | null;
  username: string;
  followers: number | null;
  niche: string | null;
  growth: number | null;
  is_own: boolean;
  bio: string | null;
  account_type: string | null;
  engagement_rate: number | null;
  avg_likes: number | null;
  avg_comments: number | null;
  tracked_at: string | null;
}

export type FeedbackType = 'thumbs_up' | 'thumbs_down' | 'note';

export interface FeedbackEntry {
  id: number;
  strategy_id: number;
  account_id: number | null;
  feedback_type: FeedbackType;
  source: string;
  notes: string | null;
  metric_snapshot: Record<string, unknown> | null;
  created_at: string;
}

export interface ProfileKit {
  id: number;
  strategy_id: number;
  kit_number: number;
  usernames: string[];
  bio: string | null;
  profile_pic_prompt: string | null;
  link_in_bio_strategy: string | null;
  highlight_themes: string[];
  content_pillars: string[];
  posting_frequency: string | null;
  created_at: string;
}

export type ProfileType = 'personal' | 'theme' | 'mixed';
export type KitPlatform = 'instagram' | 'linkedin';

export interface ProfileKitV2 {
  id: number;
  strategy_id: number;
  parent_kit_id: number | null;
  version: number;
  profile_type: ProfileType;
  platform: KitPlatform;
  usernames: string[];
  seo_name: string | null;
  bio: string | null;
  profile_pic_prompt: string | null;
  link_in_bio_strategy: string | null;
  highlight_themes: string[];
  content_pillars: string[];
  posting_frequency: string | null;
  personality_traits: string[] | null;
  origin_story_hook: string | null;
  face_style_prompt: string | null;
  color_palette: string[] | null;
  brand_aesthetic: string | null;
  series_names: string[] | null;
  starred: boolean;
  model_used: string | null;
  preset_id: number | null;
  custom_rules: string | null;
  refinement_feedback: string | null;
  created_at: string;
}

export interface StrategyAsset {
  id: number;
  strategy_id: number;
  asset_type: 'logo' | 'highlight_cover' | 'banner';
  platform: KitPlatform;
  theme_name: string | null;
  svg: string | null;
  file_url: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface KitPreset {
  id: number;
  strategy_id: number;
  name: string;
  description: string | null;
  prompt_snippet: string;
  model_preference: 'haiku' | 'sonnet';
  temperature_hint: number;
  is_default: boolean | number;
}

export interface KitGenerationLog {
  id: number;
  strategy_id: number;
  kit_id: number | null;
  profile_type: ProfileType;
  preset_name: string | null;
  model_used: string;
  prompt_snapshot: string;
  action: 'generate' | 'regenerate' | 'refine';
  refinement_feedback: string | null;
  created_at: string;
}

export interface KitCounts {
  today: number;
  total: number;
}

export interface KitFieldRule {
  label: string;
  rules: string[];
  examples: string[];
}

export type AuditPlatform = 'instagram' | 'linkedin' | 'landingpage';

export interface AuditFieldResult {
  status: 'green' | 'yellow' | 'red' | 'concern';
  current: string;
  suggestion: string;
  action_allowed?: boolean;
}

export interface AuditResult {
  overall_score: number;
  strategy_fit_score?: number | null;
  profile_audit: {
    fields: Record<string, AuditFieldResult>;
    summary: string;
  };
  feed_audit?: {
    aesthetic_match: 'green' | 'yellow' | 'red';
    notes: string;
  };
  top_priorities: string[];
}

export interface AuditHistoryEntry {
  id: number;
  strategy_id: number;
  audit_type: string;
  platform: string;
  overall_score: number;
  strategy_fit_score: number | null;
  created_at: string;
}

export interface StrategyPerformance {
  account_count: number;
  avg_likes: number | null;
  avg_comments: number | null;
  avg_viral_score: number | null;
  avg_engagement_rate: number | null;
  avg_growth: number | null;
  composite_score: number | null;
  feedback_counts: Record<string, number>;
}

export interface LeaderboardEntry extends Strategy {
  performance: StrategyPerformance;
  composite_score: number;
}

export interface HeatmapData {
  strategy_id: number;
  strategy_name: string;
  post_type: string;
  avg_viral: number;
  count: number;
}

export interface TimeseriesPoint {
  date: string;
  avg_er: number | null;
  avg_growth: number | null;
}

export interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
  // Optional inline action (R7: PATCH-failure retry). When set, the toast
  // renders a button next to the dismiss icon; clicking it invokes onAction
  // and dismisses the toast. Sticky toasts (no auto-dismiss) opt in via
  // `sticky: true` so the user has time to react.
  action?: {
    label: string;
    onAction: () => void;
  };
  sticky?: boolean;
}

// ---------------------------------------------------------------------------
// Sales CRM types
// ---------------------------------------------------------------------------

export type LeadStatus = 'new' | 'called' | 'follow_up' | 'won' | 'lost';

// Was nach einem Anruf wählbar ist. Bis 2026-08-12 waren es drei Gesprächsausgänge und
// „nicht erreicht"; alles dazwischen — Sekretariat hat abgeblockt, falscher Ansprechpartner,
// Mailbox, Anschluss existiert gar nicht — landete in der Notiz und war damit nicht auswertbar.
// Deshalb neun statt fünf. Sie zerfallen in zwei Gruppen, und die Trennlinie ist nicht Geschmack,
// sondern die Frage, die jede Auswertung stellt: War überhaupt jemand am Apparat?
export type CallOutcome =
  | 'WON' | 'LOST' | 'FOLLOW UP' | 'FALSCHER ANSPRECHPARTNER' | 'SEKRETARIAT'
  | 'NICHT ERREICHT' | 'MAILBOX' | 'NUMMER FALSCH' | 'NUMMER NICHT VERGEBEN';

// Der Befund, der aus dem Ergebnis an der GEWÄHLTEN NUMMER abfällt (`lead_phones.defect_reason`).
// Teilmenge von `PhoneDefectReason` in services/leadGenApi.ts — bewusst hier nochmal ausgeschrieben,
// damit die Typendatei nicht auf den Service zeigt; die Zuweisung im Cockpit prüft der Compiler.
export type CallOutcomeDefekt = 'falsche_nummer' | 'nicht_vergeben' | 'nicht_zustaendig';

export interface CallOutcomeMeta {
  label: string;
  /** Taste im Abschluss-Panel (nur dort belegt, Ziffern gehören den Emotionen). */
  hotkey: string;
  /** 'gespraech' = jemand war dran. Bestimmt die Erreichbarkeitsquote, siehe `giltAlsErreicht`. */
  gruppe: 'gespraech' | 'kein_gespraech';
  /** Farbfamilie für Knopf (Cockpit) und Pille (Historie) — beide lesen dieselbe Zeile. */
  ton: 'gut' | 'offen' | 'schlecht' | 'umweg' | 'still' | 'defekt';
  /** Zeitwahl im Abschluss zeigen? Bei erledigt/tot wäre ein nächster Anruf Lärm. */
  wiedervorlage: boolean;
  /** Wenn gesetzt: die angeklickte Nummer bekommt diesen Defekt-Vermerk. */
  defekt?: CallOutcomeDefekt;
}

// Reihenfolge = Reihenfolge im Abschluss-Panel.
export const CALL_OUTCOMES: readonly CallOutcome[] = [
  'WON', 'LOST', 'FOLLOW UP', 'FALSCHER ANSPRECHPARTNER', 'SEKRETARIAT',
  'NICHT ERREICHT', 'MAILBOX', 'NUMMER FALSCH', 'NUMMER NICHT VERGEBEN',
];

export const CALL_OUTCOME_META: Record<CallOutcome, CallOutcomeMeta> = {
  'WON':       { label: 'WON',       hotkey: 'W', gruppe: 'gespraech', ton: 'gut',      wiedervorlage: false },
  'LOST':      { label: 'LOST',      hotkey: 'L', gruppe: 'gespraech', ton: 'schlecht', wiedervorlage: false },
  'FOLLOW UP': { label: 'FOLLOW UP', hotkey: 'F', gruppe: 'gespraech', ton: 'offen',    wiedervorlage: true },

  // Jemand war dran, nur nicht der Richtige. Zählt als erreicht — die Nummer funktioniert, das
  // Gespräch hat stattgefunden. Der Vermerk 'nicht_zustaendig' wirkt nur auf DIESE Personenzeile
  // (scope 'person' in db_web/lead_gen.PHONE_DEFECT_REASONS), nicht auf den ganzen Anschluss.
  'FALSCHER ANSPRECHPARTNER': {
    label: 'Falscher Ansprechpartner', hotkey: 'A', gruppe: 'gespraech', ton: 'umweg',
    wiedervorlage: true, defekt: 'nicht_zustaendig',
  },
  // Vorzimmer blockt. Kein Defekt: das Sekretariat ist der Weg hinein, nicht ein kaputter Anschluss
  // — es beim ersten Abblocken zu sperren, schnitte den Lead ab. Wiedervorlage ist hier der Hebel
  // (fraud0-Kadenz greift, 'sekretariat' steht in `_CALL_RETRY_STATUSES`).
  'SEKRETARIAT': {
    label: 'Sekretariat', hotkey: 'S', gruppe: 'gespraech', ton: 'umweg', wiedervorlage: true,
  },

  'NICHT ERREICHT': { label: 'Nicht erreicht', hotkey: 'N', gruppe: 'kein_gespraech', ton: 'still', wiedervorlage: true },
  // Mailbox ist „niemand dran" mit Beleg, dass die Nummer lebt. Deshalb kein Defekt, aber auch
  // kein erreicht: sonst höbe jeder Anrufbeantworter die Erreichbarkeitsquote.
  'MAILBOX':        { label: 'Mailbox',        hotkey: 'M', gruppe: 'kein_gespraech', ton: 'still', wiedervorlage: true },

  // Die beiden Anschluss-Befunde: keine Aussage über ein Gespräch, sondern über die Leitung.
  // Beide sperren die Nummer lead-weit (scope 'number') und planen KEINE Wiedervorlage — sonst
  // käme derselbe tote Anschluss in drei Tagen wieder hoch. Getrennt, weil sie Verschiedenes über
  // die Datenqualität sagen: falsch notiert vs. Anschluss existiert nicht.
  'NUMMER FALSCH': {
    label: 'Nummer falsch', hotkey: 'X', gruppe: 'kein_gespraech', ton: 'defekt',
    wiedervorlage: false, defekt: 'falsche_nummer',
  },
  'NUMMER NICHT VERGEBEN': {
    label: 'Nummer nicht vergeben', hotkey: 'V', gruppe: 'kein_gespraech', ton: 'defekt',
    wiedervorlage: false, defekt: 'nicht_vergeben',
  },
};

// Ergebnisse, bei denen NIEMAND am Apparat war. Die Erreichbarkeitsquote der Setter rechnet
// „erreicht = alles ausser diesen" — stünde 'NUMMER FALSCH' nicht hier, machte jede tote Nummer
// die Quote besser, je öfter man sie wählt. Abgeleitet statt gepflegt, damit ein neues Ergebnis
// nicht an zwei Orten einsortiert werden muss.
export const OHNE_GESPRAECH: readonly CallOutcome[] =
  CALL_OUTCOMES.filter((o) => CALL_OUTCOME_META[o].gruppe === 'kein_gespraech');

// Ein Anruf OHNE Ergebnis zählt wie bisher als erreicht — das ist keine Aussage, sondern eine
// Lücke, und sie hier umzudrehen würde rückwirkend alle bisherigen Quoten verschieben.
export function giltAlsErreicht(outcome: string | null | undefined): boolean {
  return !OHNE_GESPRAECH.includes(outcome as CallOutcome);
}
export type LeadSource = 'linkedin' | 'instagram' | 'referral' | 'cold' | 'website' | 'job_posting' | 'fraud0';
export type EmotionTag = 'warm' | 'neutral' | 'cold' | 'hostile';

export interface JobPosting {
  id: number;
  lead_id: number;
  job_title: string;
  job_description: string | null;
  job_url: string | null;
  job_location: string | null;
  job_site: string | null;
  salary_info: string | null;
  found_at: string;
}

export interface FitScore {
  id: number;
  lead_id: number;
  strategy_id: number;
  strategy_name?: string;
  fit_score: number;
  fit_reasoning: string | null;
  fit_keywords: string | null;
  scored_at: string;
}

export interface Lead {
  id: number;
  name: string;
  company: string;
  source: LeadSource;
  source_url: string | null;
  website: string | null;
  email: string | null;
  phone: string | null;
  status: LeadStatus;
  follow_up_date: string | null;
  niche: string | null;
  assigned_to: number | null;
  created_at: string;
  call_count?: number;
  last_emotion?: EmotionTag | null;
  best_fit_score: number | null;
  best_fit_strategy: string | null;
  job_postings?: JobPosting[];
  fit_scores?: FitScore[];
  fraud0_lead_id?: number | null;  // back-link when created via the Fraud0 Telefon → Sales CRM handoff
  // Auto-Fill-Felder fürs Call-Skript (aus der fraud0-Bridge): job_title, company, paid_share, display_share.
  script_context?: {
    contact_name?: string | null;
    job_title?: string | null;
    company?: string | null;
    paid_share?: number | null;
    display_share?: number | null;
    recoverable_eur?: number | null;
    // [Domain] im Vorzimmer-Skript ("Vorname.Nachname@[Domain]") — aus lead.website abgeleitet.
    domain?: string | null;
  } | null;
}

export interface SaleCall {
  id: number;
  lead_id: number;
  date: string;
  length_seconds: number | null;
  outcome: CallOutcome | null;
  emotions: EmotionTag[];
  notes: string | null;
  caller_id: string | null;
  caller_user_id: number | null;
  caller_name?: string | null;
  is_duplicate_dial?: number; // 1 = rapid no-answer re-dial, excluded from analytics
  script_variant_id: number | null;
  created_at: string;
  lead_name?: string;
  lead_company?: string;
  lead_source?: LeadSource;
}

export interface TimingBucket {
  calls: number;
  won: number;
  lost: number;
  conversion_pct: number;
}

export interface WeekdayInsight extends TimingBucket {
  weekday: number; // 0=Sunday … 6=Saturday (SQLite strftime %w)
}

export interface HourInsight extends TimingBucket {
  hour: number; // 0–23, caller-local
}

export interface CallerInsight extends TimingBucket {
  caller: string | null;
  caller_user_id: number | null;
  avg_length_seconds: number | null;
}

export interface TimingInsights {
  by_weekday: WeekdayInsight[];
  by_hour: HourInsight[];
  by_caller: CallerInsight[];
}

export type CorrelationStrength = 'stark' | 'mittel' | 'schwach';

export interface Correlation {
  id: string; // e.g. "weekday:2", "caller:jonathan", "emotion:warm"
  dimension: string;
  dimension_label: string;
  segment_label: string;
  calls: number;
  won: number;
  conversion_pct: number;
  baseline_pct: number;
  lift_pp: number; // percentage points vs. baseline
  p_value: number;
  significant: boolean;
  strength: CorrelationStrength;
  direction: 'positive' | 'negative' | 'neutral';
}

export interface CorrelationsResult {
  baseline_pct: number;
  total_calls: number;
  min_sample: number;
  correlations: Correlation[];
}

export interface ScriptVersion {
  id: number;
  content: string;
  version: number;
  created_at: string;
}

export interface SalesKpis {
  total_calls: number;
  won: number;
  lost: number;
  conversion_pct: number;
}

// ---------------------------------------------------------------------------
// Sales Script Darwin Evolution types
// ---------------------------------------------------------------------------

// 'main' = kanonischer Champion (genau einer pro Niche+Sprache); 'fork' = persönliche Setter-Version,
// die nicht-überschriebene Blöcke live von 'main' erbt.
export type VariantStatus = 'draft' | 'active' | 'archived' | 'evolved' | 'fallback' | 'paused' | 'main' | 'fork';

export interface NicheSettings {
  id?: number;
  niche: string;
  autopilot_enabled: number;
  max_evolutions_per_week: number;
  last_evolution_at?: string;
  updated_at: string;
}

export interface ScriptSection {
  title: string;
  body: string;
}

// Art eines Blocks: 'flow' zählt im Hauptfortschritt des Calls, 'objection' ist eine per Hotkey
// erreichbare Abzweigung, 'note' ist eine Randkarte (kein Sprechtext, zählt nicht im Fortschritt).
export type BlockKind = 'flow' | 'objection' | 'note';

// Die EINE Struktur-Achse des Skripts (Gegenstück zu db_web/sales_crm.py, Abschnitt
// "Block-Metadaten"). Eine Playbook-Ebene darüber gab es bis 2026-08-07; sie duplizierte `group`.
export interface BlockMeta {
  group?: string | null;
  kind?: BlockKind;
  draft?: boolean;
  // Welche Einwände an diesem Flow-Block hängen (Reihenfolge = Anzeigereihenfolge im Cockpit).
  objections?: string[];
  // Folge-Einwand einer Antwortkette: „kein Interesse" → Stufe 1 → Stufe 2 → Stufe 3.
  next?: string | null;
  // Flow-Block, den der Setter im Call überspringen darf (Vorzimmer — wer direkt durchgestellt
  // wurde, hat ihn nie gebraucht).
  optional?: boolean;
}

export interface ScriptVariant {
  id: number;
  name: string;
  niche: string;
  language: string;
  status: VariantStatus;
  generation: number;
  parent_variant_ids: number[];
  sections_data: Record<string, ScriptSection>;
  section_order: string[];
  source_variant_id: number | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  // Git-Schicht: bei einem Fork die Keys der Blöcke, die vom Main abweichen (der Rest wird geerbt).
  overridden_sections?: string[];
  // Pro Fork ausgeblendete Blöcke · persistent, werden im Call übersprungen.
  hidden_sections?: string[];
  // Nur auf der Main gepflegt (admin/HoS) — siehe BlockMeta oben.
  block_meta?: Record<string, BlockMeta>;
}

// Skript-Bibliothek: geteilter Pool ganzer Skripte. Jeder kann einstellen (manuell oder automatisch
// beim Bearbeiten des eigenen Skripts) und fremde übernehmen.
export interface LibraryScript {
  id: number;
  name: string;
  niche: string;
  language: string;
  sections_data: Record<string, ScriptSection>;
  section_order: string[];
  notes: string | null;
  created_by: number | null;
  author: string | null;
  source_fork_id: number | null;
  is_auto: boolean;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  // Nutzung: Übernahmen gesamt / aktuell aktiv + Calls im jeweiligen Übernahme-Zeitraum.
  adopted_count: number;
  active_count: number;
  adopted_by_me: boolean;
  calls: number;
  won: number;
  conversion_pct: number;
}

// Zusammengesetztes Skript (Main-Blöcke + eigene Fork-Blöcke), wie es der Setter effektiv sieht.
export interface EffectiveScript {
  sections_data: Record<string, ScriptSection>;
  section_order: string[];
  hidden_sections?: string[];
}

// Antwort von /variants/my-fork: der persönliche Fork + die Main + das effektive Skript
// + die Block-Metadaten der Main (Phase, Art, Einwände, Kette, optional).
export interface MyForkResponse {
  fork: ScriptVariant | null;
  main: ScriptVariant | null;
  script: EffectiveScript | null;
  block_meta?: Record<string, BlockMeta>;
}

// Ein Fork-Block im Block-Leaderboard (Termin-Quote einer überschriebenen Block-Version).
export interface BlockForkStat {
  fork_id: number;
  name: string | null;
  created_by: number | null;
  calls: number;
  won: number;
  conversion_pct: number;
  beats_main: boolean;
}

// Ein Block im Leaderboard: Main-Version vs. alle Fork-Versionen dieses Blocks.
export interface BlockLeaderboardEntry {
  section_key: string;
  title: string;
  main_calls: number;
  main_won: number;
  main_conversion_pct: number;
  forks: BlockForkStat[];
}

export interface BlockLeaderboard {
  main_id: number | null;
  min_calls: number;
  blocks: BlockLeaderboardEntry[];
}

export interface VariantLeaderboardEntry {
  id: number;
  name: string;
  niche: string;
  generation: number;
  status: VariantStatus;
  calls: number;
  won: number;
  lost: number;
  conversion_pct: number;
  normalized_score: number;
  parent_variant_ids: number[];
  created_at: string;
}

export interface CallScript {
  id: number;
  call_id: number;
  variant_id: number;
  sections_data: Record<string, ScriptSection>;
  section_order: string[];
  personalization_context: Record<string, unknown> | null;
  created_at: string;
}

export interface SectionTags {
  strongest: string | null;
  weakest: string | null;
}

export interface EvolutionSuggestion {
  should_suggest: boolean;
  variant_ids: number[];
  reason: string;
}

export interface ObjectionCategory {
  id: number;
  name: string;
  description?: string;
  niche?: string;
  parent_category_id?: number;
  is_active: number;
  created_at: string;
}

export interface CallObjection {
  id: number;
  call_id: number;
  objection_text: string;
  category_id?: number;
  category_name?: string;
  confidence: number;
  flagged_for_review: number;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Content Studio types
// ---------------------------------------------------------------------------

export interface ImageTemplate {
  id: string;
  name: string;
  layout: string;
  needs_image: boolean;
  aspect_ratio?: string;
  max_chars?: number;
  category: string;
  content_format?: string;
  theme_family?: string;
  // Phase 1.5: post-universalization the API returns the full universal key
  // set for any HTML template, so this list is always populated for
  // html-rendered templates and empty for PIL templates. The frontend uses
  // its presence as a proxy for "live editor available" but actual gating
  // happens per-feature based on slide capability (photo_id, free toggle).
  supports_live_overrides?: string[];
  renderer?: 'html' | 'pil';
  supports_hook_first?: boolean;
  design_family?: string;
}

export type SlideTextOverrideKey = 'headline_text' | 'body_text' | 'eyebrow_text';

export type SlideFreePositionKey =
  | 'headline_free'
  | 'body_free'
  | 'headline_top_pct'
  | 'headline_left_pct'
  | 'body_top_pct'
  | 'body_left_pct';

export type SlidePhotoOverrideKey =
  | 'photo_pos_x_pct'
  | 'photo_pos_y_pct'
  | 'photo_zoom_pct';

export type SlideOverrideKey =
  | 'pad'
  | 'headline_size'
  | 'eyebrow_size'
  | 'brand_size'
  | 'bg_color'
  | SlideTextOverrideKey
  | SlideFreePositionKey
  | SlidePhotoOverrideKey;

export interface SlideOverrides {
  pad?: number;
  headline_size?: number;
  eyebrow_size?: number;
  brand_size?: number;
  bg_color?: string;
  headline_text?: string;
  body_text?: string;
  eyebrow_text?: string;
  // Free-position: 0/1 toggle + percentage coords. Persisted as numbers so the
  // server's strict normalize_slide_overrides keeps them through round-trips.
  headline_free?: number;
  body_free?: number;
  headline_top_pct?: number;
  headline_left_pct?: number;
  body_top_pct?: number;
  body_left_pct?: number;
  // Photo pan/zoom: pos_*_pct in 0..100, zoom in 100..400.
  photo_pos_x_pct?: number;
  photo_pos_y_pct?: number;
  photo_zoom_pct?: number;
}

export type ChartType = 'bar' | 'compare' | 'progress' | 'ranking' | 'trend';
export type ChartStylePreset =
  | 'consulting_navy'
  | 'tech_mono'
  | 'editorial_cream'
  | 'corporate_blue'
  | 'minimal_mono';

export interface ChartDataPoint {
  label: string;
  value: number;
}

export interface BubbleOverlayMessage {
  text: string;
  side?: 'left' | 'right';
  negative?: boolean;
}

export interface BubbleOverlay {
  id?: string;
  type: 'chat_bubble';
  style?: 'tinder' | 'imessage' | 'hinge';
  position_mode?: 'anchor' | 'free';
  position?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  top_pct?: number;
  left_pct?: number;
  messages: BubbleOverlayMessage[];
}

export interface ChartOverlay {
  id?: string;
  type: 'chart';
  chart_type: ChartType;
  data: ChartDataPoint[];
  style_preset: ChartStylePreset;
  position_mode: 'free';
  top_pct: number;
  left_pct: number;
  width_pct: number;
  height_pct: number;
}

export type DiagramType =
  | 'flow'
  | 'steps'
  | 'comparison'
  | 'pyramid'
  | 'cycle'
  | 'list_icons'
  | 'quote_highlight'
  | 'hierarchy';

export interface DiagramItem {
  label: string;
  description?: string;
  icon?: string;
}

export interface DiagramOverlay {
  id?: string;
  type: 'diagram';
  diagram_type: DiagramType;
  style_preset: ChartStylePreset;
  items?: DiagramItem[];
  left_title?: string;
  right_title?: string;
  left_items?: DiagramItem[];
  right_items?: DiagramItem[];
  quote_text?: string;
  quote_author?: string;
  root_label?: string;
  position_mode: 'free';
  top_pct: number;
  left_pct: number;
  width_pct: number;
  height_pct: number;
}

export interface ImessageMessage {
  side: 'left' | 'right';
  text: string;
  style?: 'imessage' | 'sms';
}

export type ImessageDeviceColor = 'black' | 'blue' | 'pink' | 'green' | 'natural';
export type ImessageNetwork = 'LTE' | '5G' | '4G' | 'WiFi';

export interface ImessageMockupOverlay {
  id?: string;
  type: 'imessage';
  position_mode: 'free';
  top_pct: number;
  left_pct: number;
  width_pct: number;
  height_pct: number;
  // Status bar
  time: string;
  carrier: string;
  network: ImessageNetwork;
  signal_bars: 1 | 2 | 3 | 4;
  battery_pct: number;
  // Header
  contact_name: string;
  // Body
  messages: ImessageMessage[];
  // Hardware frame
  show_device_frame: boolean;
  device_color?: ImessageDeviceColor;
}

export type SlideOverlay = BubbleOverlay | ChartOverlay | DiagramOverlay | ImessageMockupOverlay;

// ── Phone Mockup (iMessage / Tinder) · block-mode replacement for ImessageMockupOverlay
export type PhoneMockupKind = 'imessage' | 'tinder_chat' | 'tinder_match';

export interface PhoneMockupMessage {
  side: 'left' | 'right';
  text: string;
  style?: 'imessage' | 'sms' | 'tinder';
}

export interface PhoneMockupSpec {
  kind: PhoneMockupKind;
  device_color?: ImessageDeviceColor;
  show_device_frame?: boolean;
  // Status bar
  time?: string;
  network?: ImessageNetwork;
  signal_bars?: 1 | 2 | 3 | 4;
  battery_pct?: number;
  // Chat content (imessage / tinder_chat)
  contact_name?: string;
  contact_avatar_url?: string | null;
  messages?: PhoneMockupMessage[];
  // Tinder-match screen
  match_photo_left_url?: string | null;
  match_photo_right_url?: string | null;
  match_subtitle?: string | null;
}

export type PhoneMockupArrangement =
  | 'text_left_phone_right'
  | 'text_top_phone_bottom'
  | 'phone_top_text_bottom';

// ── Diagram v2 (production migration: AI-driven nodes/edges) ──────────────

export type DiagramV2Type = 'flow' | 'hierarchy' | 'cycle';
export type DiagramV2ImageMode = 'replace' | 'icon-top';

export interface DiagramV2NodeImage {
  url: string;          // data:image/svg+xml;base64,... or /api/diagram-assets/...
  mode: DiagramV2ImageMode;
}

export interface DiagramV2Node {
  id: string;
  label: string;
  description?: string;
  image?: DiagramV2NodeImage;
}

export interface DiagramV2Edge {
  from: string;
  to: string;
  label?: string;
}

export interface DiagramV2 {
  version: 'v2';
  diagram_type: DiagramV2Type;
  style_preset: ChartStylePreset;
  nodes: DiagramV2Node[];
  edges: DiagramV2Edge[];
  layout?: unknown;     // computed server-side; opaque to FE
}

export interface DiagramV2SuggestResponse extends DiagramV2 {
  image_match_log: { node_id: string; label: string; matched_url: string | null }[];
}

export interface DiagramAssetLibraryEntry {
  keyword: string;
  asset_url: string;
  synonyms: string[];
}

export interface DiagramAssetLibraryResponse {
  version: string;
  scope: 'global' | 'user';
  assets: DiagramAssetLibraryEntry[];
}

export interface CustomTemplate {
  id: number;
  name: string;
  base_template_id: string;
  overrides: SlideOverrides;
  created_at?: string;
}

export interface CarouselSlide {
  id: number;
  generation_id: number;
  slide_number: number;
  slide_text: string;
  slide_role?: string;
  image_path: string | null;
  template_id: string | null;
  photo_id?: number | null;
  created_at: string;
  updated_at: string;
  rendered_at: string | null;
  is_stale?: number;
  theme_family_override?: string | null;
  effective_theme_family?: string;
  slide_overrides?: SlideOverrides;
  slide_data?: string | { overlays?: SlideOverlay[] } | null;
}

export interface CarouselData {
  slides: CarouselSlide[];
  carousel_caption: string | null;
  carousel_hashtags: string | null;
  template_id: string | null;
  theme_family_override?: string | null;
  bg_color_override?: string | null;
  cover_template_id?: string | null;
  /** Design-Decoupling: visual skin preset id. NULL = legacy family-based render. */
  design_id?: string | null;
}

export type ContentType =
  | 'hook' | 'caption' | 'full_post' | 'video_drehplan' | 'carousel_concept'
  | 'stat_post' | 'split_post' | 'checklist_post' | 'cta'
  // Reel-formats (video pipeline) · distinct from image-post types above.
  | 'hook_reel' | 'b_roll_reel' | 'talking_head'
  | 'tutorial_reel' | 'stat_reel' | 'story_reel' | 'cta_reel'
  // Generic rendered reel from the Reel-Builder (shown in Verlauf, format=video).
  | 'reel';

export type ReelType = Extract<
  ContentType,
  'hook_reel' | 'b_roll_reel' | 'talking_head' | 'tutorial_reel' | 'stat_reel' | 'story_reel' | 'cta_reel'
>;

export type CartesiaVoice = 'alina' | 'marlene' | 'viktoria' | 'sebastian' | 'moritz' | 'henrik';
export type MusicMood = 'energetic' | 'cinematic' | 'soft' | 'none';
export type BRollSource = 'stock' | 'generated' | 'manual' | 'library';
export type CaptionStyle = 'pill' | 'minimal' | 'off';
export type ReelEmotion = 'frustrated' | 'hopeful' | 'confident' | 'curious';

export interface VideoSettings {
  voiceoverEnabled: boolean;
  voiceId: CartesiaVoice;
  musicMood: MusicMood;
  // Trending-Track-Referenz (3D). Seedet draft.music_id. Audio wird NATIV in IG
  // hinzugefuegt, nie ins MP4 gemuxt. null = kein Track gewaehlt.
  musicTrackId: number | null;
  bRollSource: BRollSource;
  libraryVideoUrl: string | null;
  captionStyle: CaptionStyle;
  emotion: ReelEmotion;
  handle: string | null;
}

export type RenderStatus = 'submitted' | 'render_unavailable' | 'submit_failed';

export interface ReelBriefPreview {
  hook_text: string;
  body_beats: string[];
  cta_text: string;
}

export interface GenerateReelResponse {
  generation_id: number;
  render_status: RenderStatus;
  job_id: string | null;
  props_hash: string;
  brief: ReelBriefPreview;
  voice_url: string | null;
  music_url: string | null;
  bg_image_count: number;
  error?: string | null;
}

export type ContentRatingValue = 'good' | 'bad' | 'adjust';

export interface BatchTopic {
  topic_angle: string;
  content_type: ContentType;
  notes?: string;
  content_format?: string;
  content_lang?: string;
}

export interface MultiPlatformGenerationRequest {
  topic_angle: string;
  platforms: string[];
  content_lang?: string;
  competitor_post_ids?: number[];
  preset_id?: number;
  custom_rules?: string;
  model?: string;
}

export interface StatVariant {
  text: string;
  template_id: string | null;
  source_ref: string | null;
}

export interface ContentGeneration {
  id: number;
  strategy_id: number;
  parent_generation_id: number | null;
  version: number;
  content_type: ContentType;
  style_preset: string | null;
  custom_rules: string | null;
  niche_directive: string | null;
  competitor_post_ids: number[] | null;
  output_text: string | null;
  display_text?: string | null;
  model_used: string | null;
  refinement_feedback: string | null;
  chain_summary: string | null;
  starred: boolean;
  account_id: number | null;
  template_id: string | null;
  carousel_caption: string | null;
  carousel_hashtags: string | null;
  created_at: string;
  content_format?: string | null;
  content_lang?: string | null;
  suggested_template?: string;
  experiments?: { toggles: string[]; topic_angle?: string | null } | null;
  /** Enriched stat variants from API response · not persisted in DB, only present on fresh generates */
  stat_variants?: StatVariant[];
  /** True if any carousel slide for this generation has is_stale = 1 */
  has_stale_slides?: boolean;
  /** Plan slot assigned via auto_slot · present on fresh generates (POST /generate response), not on history rows */
  plan_slot_id?: number | null;
  slot_label?: string | null;
  scheduled_at?: string | null;
  /** Document insights consumed by this generation, with read-time access redaction */
  used_insights?: UsedInsight[];
  /** Per-slide source map (0-based stringified slide index → list of sources). Empty for legacy generations. */
  slide_sources?: Record<string, SlideSource[]>;
  /** Design-Decoupling: visual skin id (midnight_oath, editorial, leitkraft, ...). NULL = legacy family-based render. */
  design_id?: string | null;
  /** Phase 10 (May 2026): aggregated AI cost from content_generation_log. Zero for older generations from before cost-tracking. */
  ai_cost?: {
    total_usd: number;
    input_tokens: number;
    output_tokens: number;
    cache_read_tokens: number;
    cache_creation_tokens: number;
    calls: number;
  };
  /** Datenfluss-Artefakte: was in diese Generierung floss (Evidence, Viral-Hook, ...). */
  artifacts?: import('../services/contentStudioApi').PromptArtifacts;
}

export interface DesignPreset {
  id: string;
  label: string;
  description: string;
  tokens: Record<string, string | number>;
  niche_affinity: string[];
}

export type BlockLayoutRole = 'hook' | 'body' | 'cta';

export interface BlockLayout {
  id: string;
  label: string;
  roles: BlockLayoutRole[];
  preview: string;
  block_payload_shape: string[];
  description: string;
  /** Whether the layout has a corresponding preset in services/block_layout_presets.py.
   *  When false, the picker shows it greyed-out (no apply possible).
   *  Optional for forward-compat: old backends without this field default to true. */
  applicable?: boolean;
}

export interface SlideSource {
  insight_id: number;
  title: string | null;
  document_title: string | null;
  /** Capped at 180 chars server-side. */
  snippet: string;
}

export interface ContentStylePreset {
  id: number;
  strategy_id: number;
  name: string;
  description: string | null;
  prompt_snippet: string;
  is_default: boolean | number;
  created_at: string;
}

export interface ContentRating {
  id: number;
  generation_id: number;
  rating: ContentRatingValue;
  notes: string | null;
  created_at: string;
}

export interface ContentRatingsSummary {
  total: number;
  good_count: number;
  bad_count: number;
  adjust_count: number;
  by_content_type: Record<string, number>;
}

export interface EngagementData {
  likes: number;
  comments: number;
  views: number;
  engagement_rate: number;
}

export interface ContentPosted {
  id: number;
  generation_id: number;
  post_url: string;
  posted_at: string;
  scrape_intervals: string[];
  engagement_24h: EngagementData | null;
  engagement_72h: EngagementData | null;
  engagement_7d: EngagementData | null;
  created_at: string;
}

export type AuditAction = 'kit' | 'implemented' | 'dismissed' | 'custom';

export interface AuditSuggestionAction {
  id: number;
  audit_id: number;
  strategy_id: number;
  field_name: string;
  action: AuditAction;
  original_suggestion: string | null;
  custom_value: string | null;
  target_kit_id: number | null;
  created_at: string;
}

export interface CurrentProfile {
  id: number;
  strategy_id: number;
  username: string | null;
  seo_name: string | null;
  bio: string | null;
  profile_pic_description: string | null;
  highlight_themes: string[];
  extra_fields: Record<string, unknown>;
  source: string;
  last_audit_id: number | null;
  updated_at: string;
}

export interface AuditImplementationScore {
  total: number;
  implemented: number;
  audit_id: number | null;
}

export interface ContentExperiment {
  id: number
  strategy_id: number
  generation_id: number | null
  content_type: string
  tension_category: string
  hook_formula: string
  changed_variable: 'tension' | 'hook_formula' | null
  previous_experiment_id: number | null
  is_manual: boolean
  performance_score: number | null
  performance_final: boolean
  confidence: 'none' | 'low' | 'medium' | 'high'
  saves_count: number | null
  created_at: string
}

export interface ExperimentSuggestion {
  tension_category: string
  hook_formula: string
  changed_variable: string | null
  previous_experiment_id: number | null
  is_exploit: boolean
  reasoning: string
  coverage: { tested: number; total: number; percent: number }
  phase: 'cold_start' | 'exploration' | 'exploitation'
}

export interface ExperimentMatrixCell {
  count: number
  avg_score: number | null
  final_count: number
  best: number | null
  worst: number | null
}

export interface ExperimentMatrix {
  cells: Record<string, Record<string, ExperimentMatrixCell>>
  by_tension: Record<string, { count: number; avg_score: number | null }>
  by_hook: Record<string, { count: number; avg_score: number | null }>
  top_3: Array<{ tension: string; hook: string; avg_score: number; count: number }>
  worst_3: Array<{ tension: string; hook: string; avg_score: number; count: number }>
  total_experiments: number
  total_with_score: number
}

export interface WinningPatterns {
  best_tension: string | null
  best_hook: string | null
  best_combo: { tension: string; hook: string; avg_score: number } | null
  avoid: string[]
  insight: string
}

export interface NicheInsights {
  best_tension: string | null
  best_hook: string | null
  best_combo: { tension: string; hook: string; avg_score: number } | null
  avoid: string[]
  insight: string
  strategy_count: number
  total_final: number
}

// ---------------------------------------------------------------------------
// Image Picker types
// ---------------------------------------------------------------------------

export interface StockWarning {
  source: string;
  code: string;
  message: string;
}

export interface StockPhoto {
  url: string;
  thumb_url: string;
  photographer: string;
  source: string;
  width: number;
  height: number;
  attribution_url: string | null;
}

export interface AiGeneratedImage {
  url: string;
  width: number;
  height: number;
  model: string;
  seed: number;
  prompt_used: string;
}

export interface StrategyDocument {
  id: number;
  strategy_id: number;
  original_filename: string;
  file_type: string;
  file_size: number;
  extraction_status: 'pending' | 'processing' | 'done' | 'error';
  extraction_error: string | null;
  created_at: string;
  insight_count?: number;
  niche_id?: number | null;
  default_scope?: 'private' | 'niche' | 'account';
  coverage_baseline_at?: string | null;
}

export interface StrategyDocumentDetail extends StrategyDocument {
  extracted_text: string | null;
}

export type InsightCluster = 'problem' | 'solution' | 'evidence' | 'example' | 'principle' | 'quote';
export type InsightType = 'stat' | 'claim' | 'quote' | 'case' | 'framework' | 'tip';

export type InsightScopeLevel = 'private' | 'niche' | 'account';

export interface DocumentInsight {
  id: number;
  document_id: number;
  strategy_id: number;
  cluster: InsightCluster;
  insight_type: InsightType;
  title: string;
  insight_text: string;
  source_snippet: string | null;
  source_char_start: number | null;
  source_char_end: number | null;
  product_risk: 'low' | 'medium' | 'high';
  tags: string[];
  is_active: boolean;
  created_at: string;
  scope_level?: InsightScopeLevel;
  chapter_label?: string | null;
  chapter_index?: number | null;
  section_label?: string | null;
  updated_at?: string;
  /** How often this insight has appeared in a published post (lifetime). 0 = never used in real content. */
  published_count?: number;
}

export interface UsedInsight {
  insight_id: number;
  rank: number;
  used_at: string;
  title: string;
  chapter_label: string | null;
  chapter_index: number | null;
  source_doc_id: number | null;
  source_doc_name: string | null;
  available: boolean;
  cluster?: InsightCluster;
  insight_type?: InsightType;
  snippet?: string;
}

export interface NicheRef {
  id: number;
  name: string;
  parent_niche_id: number | null;
  description: string | null;
}

export interface CoverageStats {
  doc_id: number;
  original_filename: string;
  niche_id: number | null;
  default_scope: 'private' | 'niche' | 'account';
  coverage_baseline_at: string | null;
  total: number;
  used: number;
}

export interface DocumentEvidenceItem {
  id: number;
  title: string;
  cluster: InsightCluster;
  insight_type: InsightType;
  core_concept: string;
  snippet: string;
  tags: string[];
  product_risk: 'low' | 'medium' | 'high';
  source_doc_id: number | null;
  source_doc_name: string | null;
  insight_summary: string;
}

export interface DocumentEvidenceResponse {
  items: DocumentEvidenceItem[];
  missing_insight_doc_ids: number[];
}

// ---------------------------------------------------------------------------
// Brand Entity Layer
// ---------------------------------------------------------------------------

export type EntityType = 'person' | 'business' | 'brand';
export type ChannelPlatform = 'instagram' | 'youtube' | 'linkedin' | 'tiktok' | 'website' | 'landing_page';

export interface Brand {
  id: number;
  display_name: string;
  entity_type: EntityType;
  website: string | null;
  industry: string | null;
  description: string | null;
  logo_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface BrandChannel {
  id: number;
  brand_id: number;
  platform: ChannelPlatform;
  handle: string | null;
  url: string | null;
  account_id: number | null;
  followers: number | null;
  following: number | null;
  posts_count: number | null;
  bio: string | null;
  engagement_rate: number | null;
  is_primary: number;
  last_scraped_at: string | null;
  created_at: string;
  ig_connection_id: number | null;
}

export interface BrandWithChannels extends Brand {
  channels: BrandChannel[];
}

export interface BrandCompetitor {
  id: number;
  brand_id: number;
  competitor_brand_id: number;
  competitor: Brand;
  competitor_channels: BrandChannel[];
  notes: string | null;
  created_at: string;
}

export interface BenchmarkMetric {
  metric: string;
  own_value: number | null;
  competitor_values: Array<{
    brand_id: number;
    display_name: string;
    value: number | null;
    delta: number | null;
  }>;
}

// ---------------------------------------------------------------------------
// Instagram Publishing types
// ---------------------------------------------------------------------------

export interface IgConnection {
  id: number;
  ig_business_account_id: string;
  ig_username: string | null;
  ig_profile_pic_url: string | null;
  fb_page_name: string | null;
  token_status: 'active' | 'expiring' | 'expired' | 'revoked';
  token_expires_at: string;
  connected_at: string;
  user_id: number;
}

export interface IgPublishLog {
  id: number;
  connection_id: number;
  generation_id: number | null;
  post_type: 'image' | 'carousel' | 'reel';
  status: 'publishing' | 'scheduled' | 'success' | 'error';
  permalink: string | null;
  error_message: string | null;
  ig_username?: string;
  caption?: string | null;
  created_at: string;
  published_at: string | null;
  channel_id: number;
  scheduled_at: string | null;
}

// ---------------------------------------------------------------------------
// Content Format / Plan / Performance types
// ---------------------------------------------------------------------------

export interface ContentFormatDef {
  id: string;
  label: string;
  ai_hint: string;
  template_count: number;
}

export interface TemplateCompatibility {
  min_items: number;
  max_items: number;
  requires_numeric: boolean;
  format_hint: string;
}

export interface TemplateRanking {
  template_id: string;
  score: number;
  reasoning: string;
  template_name: string;
}

export interface ContentPlan {
  id: number;
  strategy_id: number;
  name: string;
  is_active: boolean;
  cycle_type: 'weekly' | 'biweekly' | 'monthly';
  cycle_start: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContentPlanSlot {
  id: number;
  plan_id: number;
  day: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
  time_window: 'morning' | 'afternoon' | 'evening';
  content_format: string;
  content_type: string;
  config: {
    preset_id?: number;
    directive?: string;
    notes?: string;
  };
  locked_until: string | null;
  auto_adjusted: boolean;
  previous_format: string | null;
  origin?: 'auto' | 'manual';
  adjustment_reason: string | null;
  sort_order: number;
  status?: 'pending' | 'generated' | 'posted';
}

export type WeekPlanPhase = 'idle' | 'suggesting' | 'review' | 'generating' | 'done';

export interface WeekSlotSuggestion {
  slot_id: number;
  day: string; // 'monday'...'sunday'
  time_window: string; // 'morning'/'afternoon'/'evening'
  content_format: string;
  content_type: string;
  topic_angle: string;
  tension_category: string;
  hook_formula: string;
  source: 'pool' | 'sequencer' | 'persisted' | 'theme';
  pool_topic_id?: number | null;
  is_exploit?: boolean;
  reasoning?: string;
  skipped?: boolean;
  changed_variable?: string | null;
  previous_experiment_id?: number | null;
  is_schedulable?: boolean;
}

export interface WeekSlidePreview {
  slide_number: number;
  slide_text: string;
  image_path?: string | null;
  slide_role?: string | null;
  template_id?: string | null;
}

export interface WeekSlotGeneration {
  slot_id: number;
  generation_id: number | null;
  template_id: string | null;
  output_text: string;
  display_text?: string;
  slide1_text?: string;
  slide1_image_path?: string;
  slides?: WeekSlidePreview[];
  status: 'pending' | 'generating' | 'done' | 'error';
  error: string | null;
  is_scheduled?: boolean;
  publish_log_id?: number | null;
  schedule_status?: 'scheduled' | 'publishing' | 'published' | null;
  content_format?: string;
  _imageTs?: number;
}

export interface WeekPlanConfirmResult {
  generation_id: number;
  scheduled_at: string;
  publish_log_id: number;
}

export interface VideoExportRequest {
  generation_id: number;
  music_id?: number;
  transition_type?: 'slide' | 'fade' | 'zoom';
}

export interface PlanChangelog {
  id: number;
  plan_id: number;
  slot_id: number | null;
  action: string;
  old_format: string | null;
  new_format: string | null;
  reason: string | null;
  created_at: string;
}

export interface FormatPerformance {
  id: number;
  strategy_id: number;
  content_format: string;
  total_posts: number;
  avg_likes: number | null;
  avg_comments: number | null;
  avg_engagement_rate: number | null;
  trend: 'improving' | 'stable' | 'declining';
  confidence: number;
  last_calculated: string;
}

export interface CriticVerdictCounts {
  approved: number;
  regenerated: number;
  escalated: number;
  off: number;
}

export interface CriticPayoffBucket {
  payoff_type: string;
  count: number;
  regenerated: number;
  escalated: number;
  intervention_rate: number;
  escalation_rate: number;
}

export interface CriticHookCategoryBucket {
  category: string;
  count: number;
  regenerated: number;
  escalated: number;
  intervention_rate: number;
  escalation_rate: number;
}

export interface CriticTimeseriesPoint {
  date: string;
  approved: number;
  regenerated: number;
  escalated: number;
  off: number;
}

export interface CriticStats {
  window_days: number;
  total: number;
  verdict_counts: CriticVerdictCounts;
  escalation_rate: number;
  regeneration_rate: number;
  by_payoff_type: CriticPayoffBucket[];
  by_hook_category: CriticHookCategoryBucket[];
  timeseries: CriticTimeseriesPoint[];
}

export interface RuleSuggestion {
  type: string;
  from_format: string;
  to_format: string;
  reason: string;
}

export interface AIAnalysis {
  insights: string[];
  top_format: string | null;
  recommendation: string;
}

export interface IgPublishSSEEvent {
  event: 'ig_publish_update';
  log_id: number;
  generation_id: number | null;
  status: 'success' | 'error';
  permalink?: string;
  ig_username?: string;
  connection_id: number;
  error?: string;
}

// ---------------------------------------------------------------------------
// Satellite OS types
// ---------------------------------------------------------------------------

export type SatelliteStatus = 'setup' | 'growing' | 'monetizing' | 'sold' | 'archived';

export interface Satellite {
  id: number;
  name: string;
  status: SatelliteStatus;
  strategy_id: number | null;
  origin_strategy_id: number;
  account_id: number | null;
  brand_id: number | null;
  monetization_model: string | null;
  bio_link_url: string | null;
  target_follower_count: number | null;
  monthly_revenue_target: number | null;
  total_revenue: number;
  total_leads_generated: number;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  // Enriched
  strategy_name?: string;
  origin_strategy_name?: string;
  account_username?: string;
  followers?: number | null;
  stats?: SatelliteStats;
  recent_leads?: SatelliteLead[];
}

export interface SatelliteStats {
  leads_by_status: Record<string, number>;
  total_revenue: number;
  total_leads_generated: number;
  followers: number | null;
}

export interface SatelliteLead {
  id: number;
  satellite_id: number;
  source_type: string;
  ig_username: string | null;
  email: string | null;
  name: string | null;
  status: 'raw' | 'qualified' | 'promoted' | 'rejected';
  routed_to: string | null;
  notes: string | null;
  captured_at: string;
  qualified_at: string | null;
  promoted_at: string | null;
}

export interface ForkSatelliteRequest {
  origin_strategy_id: number;
  name: string;
  mode: 'ai' | 'manual';
  monetization_model?: string;
  bio_link_url?: string;
  target_follower_count?: number;
}

// ---------------------------------------------------------------------------
// Roadmap types
// ---------------------------------------------------------------------------

export interface RoadmapItem {
  id: number;
  title: string;
  description?: string;
  phase: 'short' | 'medium' | 'long' | 'deferred';
  status: 'planned' | 'in_progress' | 'done' | 'blocked' | 'deferred';
  market?: 'dach' | 'latam' | 'global';
  category?: 'system' | 'content' | 'outreach' | 'team' | 'strategy';
  sort_order: number;
  due_date?: string;
  completed_at?: string;
  created_at: string;
}

export interface FunnelData {
  market: string | null;
  discovery: number;
  outreach: number;
  responses: number;
  leads: number;
  calls: number;
  won: number;
  conversion_rates: {
    discovery_to_outreach: number;
    outreach_to_response: number;
    response_to_lead: number;
    lead_to_call: number;
    call_to_won: number;
  };
}

// ===== Costs (Kosten tab) =====

export interface Employee {
  id: number;
  name: string;
  role: string | null;
  market: 'dach' | 'latam' | 'global' | null;
  cost_mode: 'fixed' | 'hourly';
  monthly_cost: number;
  hourly_rate: number;
  hours_per_month: number;
  currency: string;
  web_user_id: number | null;
  active: number; // 0/1
  notes: string | null;
  monthly_effective: number; // serverseitig berechnet
  created_at: string;
  updated_at: string;
}

export interface FixedCost {
  id: number;
  name: string;
  amount_usd: number;
  period: string; // 'monthly' | 'yearly' | 'weekly' | 'once'
  category: string | null;
  active: number; // 0/1
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CostProvider {
  provider: string;
  cost_usd: number;
  is_estimated: boolean;
}

export interface CostsOverview {
  month_total_usd: number;
  by_provider: CostProvider[];
  daily: { day: string; value: number }[];
  fixed_costs: FixedCost[];
  fixed_monthly_total_usd: number;
  grand_total_usd: number;
  days: number;
}

// ===== Content Vault =====

export interface VaultHookAnalysis {
  text: string;
  type: string;
  formula: string;
  strength: number;
  why_it_works: string;
}

export interface VaultIdeaAnalysis {
  topic: string;
  seed: string;
  angle: string;
  contrast: { common_belief: string; reality: string };
  novelty: number;
}

export interface VaultTension {
  category: string;
  label: string;
  explanation: string;
  intensity: number;
}

export interface VaultCaptionSection {
  section: string;
  text_preview: string;
  purpose: string;
}

export interface VaultPersonalization {
  suggestion: string;
  difficulty: 'easy' | 'medium' | 'hard';
}

export interface VaultAnalysis {
  format_type: string;
  format_confidence: number;
  hook: VaultHookAnalysis;
  idea: VaultIdeaAnalysis;
  tensions: VaultTension[];
  caption_structure: VaultCaptionSection[];
  visual_layout: string;
  personalization: VaultPersonalization[];
  summary: string;
  estimated_save_rate: string;
  content_pillars_match: string[];
}

export interface VaultItem {
  id: number;
  user_id: number;
  post_id: number;
  strategy_id: number | null;
  analysis_status: 'queued' | 'analyzing' | 'succeeded' | 'failed';
  analysis: VaultAnalysis | null;
  review_status: 'unreviewed' | 'reviewed';
  is_favorite: boolean;
  is_shortlisted: boolean;
  tags: string[];
  notes: string | null;
  created_at: string;
  analyzed_at: string | null;
  // Joined from posts table
  caption: string | null;
  likes: number;
  comments: number;
  views: number | null;
  viral_score: number;
  url: string;
  posted_at: string | null;
  image_url: string | null;
  post_type: string;
  owner_username: string;
  // Joined from accounts table (optional)
  account_username?: string;
  account_niche?: string;
}

export interface VaultStats {
  total: number;
  by_analysis_status: Record<string, number>;
  by_review_status: Record<string, number>;
  favorites: number;
  shortlisted: number;
}

export interface TensionCategory {
  id: number;
  name: string;
  label: string;
  description: string | null;
  usage_count: number;
}

export interface VaultFilters {
  review_status?: string;
  is_favorite?: boolean;
  is_shortlisted?: boolean;
  strategy_id?: number;
  format_type?: string;
  tension_category?: string;
  tag?: string;
  sort: 'newest' | 'oldest' | 'viral_score';
  limit: number;
  offset: number;
}

export interface VaultQueueStatus {
  queue_size: number;
  analyzing_item_id: number | null;
}

export interface VaultAnalysisSSEEvent {
  event: 'vault_analysis_complete';
  item_id: number;
  status: 'succeeded' | 'failed';
  format_type?: string;
  error?: string;
}

// ---------------------------------------------------------------------------
// Trending Music + Video Generation types
// ---------------------------------------------------------------------------

export interface TrendingTrack {
  id: number;
  platform: 'tiktok' | 'instagram';
  track_title: string;
  artist_name: string;
  external_url: string;
  audio_url: string | null;
  cover_image_url: string | null;
  external_id: string;
  usage_count: number;
  trend_score: number;
  genre: string | null;
  duration_seconds: number | null;
  is_licensed: number;
  source: 'auto' | 'manual';
  source_query: string | null;
  first_seen_at: string;
  last_seen_at: string;
  niches?: string[];
}

export interface TrendingTrackHistory {
  id: number;
  music_id: number;
  usage_count: number;
  trend_score: number;
  scraped_at: string;
}

export interface GeneratedVideo {
  id: number;
  strategy_id: number | null;
  generation_id: number | null;
  music_id: number | null;
  video_filename: string;
  drive_file_id: string | null;
  drive_url: string | null;
  thumbnail_path: string | null;
  duration_seconds: number;
  slide_count: number;
  slide_timings: number[];
  resolution: string;
  transition_type: string;
  status: 'generating' | 'uploading' | 'ready' | 'error' | 'published';
  error_message: string | null;
  caption: string | null;
  created_at: string;
  published_at: string | null;
}

export interface GoogleDriveConnection {
  connected: boolean;
  email?: string;
  folder_id?: string | null;
  connected_at?: string;
  connection_id?: number;
}

export interface VideoSSEEvent {
  event: 'video_update';
  video_id: number;
  status: string;
  error?: string;
  drive_url?: string;
  duration_seconds?: number;
}

export interface VaultQueueSSEEvent {
  event: 'vault_queue_update';
  queue_size: number;
  analyzing_item_id?: number;
}

export interface AutopilotConfig {
  id?: number;
  channel_id: number;
  enabled: boolean;
  paused: boolean;
  warmup_phase: 'new' | 'building' | 'growing' | 'mature';
  warmup_started_at: string;
  phase_upgraded_at?: string | null;
  max_posts_per_day: number;
  preferred_times: string[];
  notify_errors: boolean;
  notify_skips: boolean;
  notify_milestones: boolean;
  notify_queue_low: boolean;
}

export interface AutopilotStatus {
  enabled: boolean;
  paused: boolean;
  warmup_phase: 'new' | 'building' | 'growing' | 'mature';
  today_count: number;
  max_per_day: number;
  scheduled_slots_count: number;
  queue_depth: number;
  prerequisites: {
    ig_connection: boolean;
    content_plan: boolean;
    has_slots: boolean;
    all_met: boolean;
  } | undefined;
}

export interface AutopilotNotification {
  id: number;
  channel_id: number;
  notification_type: 'error' | 'skip' | 'queue_low' | 'milestone' | 'daily_summary';
  title: string;
  body?: string | null;
  read: boolean;
  created_at: string;
}

export interface SkipLogEntry {
  id: number;
  channel_id: number;
  generation_id?: number | null;
  plan_slot_id?: number | null;
  skip_reason: 'visual_duplicate' | 'caption_duplicate' | 'concept_duplicate' | 'warmup_limit';
  details?: string | null;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Mind OS types
// ---------------------------------------------------------------------------

export interface MindEntry {
  id: number;
  user_id: number;
  type: 'capture' | 'decision' | 'principle' | 'domain';
  title: string;
  body: string;
  tags: string[];
  domain?: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface QuestionAnalysis {
  blind_spots: string[];
  hidden_assumptions: string[];
  better_versions: {
    version: string;
    why_better: string;
    thinking_level: 'tactical' | 'strategic' | 'meta';
    style: string;
  }[];
  cross_domain_angles: {
    domain: string;
    insight: string;
    analogon: string;
  }[];
  category: 'decision' | 'learning' | 'exploration' | 'problem-solving';
  outlier_question: string;
}

export interface QuestionSession {
  id: number;
  original_question: string;
  deep_cross_domain: boolean;
  analysis: QuestionAnalysis;
  selected_version?: string;
  copied_version?: string;
  category?: string;
  created_at: string;
}

export interface RetrospectiveAnalysis {
  decision_patterns: string[];
  question_trends: { tactical: number; strategic: number; meta: number };
  blind_spots: string[];
  domain_effectiveness: { domain: string; insight_count: number; quality: string }[];
  next_challenges: string[];
}

export interface Retrospective {
  id: number;
  period: string;
  analysis: RetrospectiveAnalysis;
  created_at: string;
}

export interface DashboardData {
  recent_captures: MindEntry[];
  retrospective_due: boolean;
  last_retrospective_at?: string;
  daily_provocation: string;
}

export interface WorldviewChallenge {
  steelman: string;
  attack: string;
  synthesis: string;
  blind_spots: string[];
}

// ---------------------------------------------------------------------------
// Translation SSE Events
// ---------------------------------------------------------------------------

export interface TranslateProgressEvent {
  event: 'translate_progress';
  strategy_id: number;
  user_id: number;
  module_id: string;
  progress: number;
  total: number;
}

export interface TranslateCompleteEvent {
  event: 'translate_complete';
  strategy_id: number;
  user_id: number;
  target_language: string;
  skipped: string[];
}

// ---------------------------------------------------------------------------
// SEO Intelligence · DataForSEO integration types
// ---------------------------------------------------------------------------

export interface SEOKeyword {
  keyword: string
  position: number
  search_volume: number
  estimated_clicks: number
  url: string
}

export interface LighthouseScores {
  performance: number
  seo: number
  accessibility: number
  best_practices: number
}

export interface SEOData {
  analyzed: boolean
  domain: string | null
  organic_traffic: number | null
  keyword_count: number | null
  backlinks_count: number | null
  referring_domains: number | null
  no_data: boolean
  analyzed_at: string | null
  top_keywords: SEOKeyword[]
  pain_points: string[]
  lighthouse: LighthouseScores | null
  cost_usd: number
}

export interface SEOBatchProgress {
  target_id: number
  done: number
  total: number
  status: 'ok' | 'no_data' | 'error'
}

export interface SEOBatchComplete {
  total: number
  success: number
  no_data: number
  errors: number
  total_cost_usd: number
}

// ── KI-Qualität (Master Control) ────────────────────────────────────────────
// Erfolg/Ausfall je KI-Schritt. Backend: db_web/ai_call_log.get_ai_call_overview
export interface AiStepStat {
  step: string;
  total: number;
  failures: number;
  avg_latency_ms: number | null;
  failure_rate_pct: number;
}

export interface AiCallError {
  step: string;
  error: string | null;
  model: string | null;
  created_at: string;
}

export interface AiStatsOverview {
  days: number;
  total_calls: number;
  total_failures: number;
  failure_rate_pct: number;
  slowest_step: string | null;
  slowest_latency_ms: number | null;
  steps: AiStepStat[];
  daily: { day: string; failures: number; total: number }[];
  recent_errors: AiCallError[];
  instrumented_steps: number;
  known_uninstrumented: string[];
}
